import dayjs from 'dayjs';

import type {
  IDeskFormatted,
  ISpace,
  SpaceValueType,
} from 'app/components/commons/Hotel/Types';
import type { FormOpeningHours } from 'app/components/commons/TimeRangeSection/utils';
import {
  formatOpeningHoursForAPI,
  parseOpeningHoursFromAPI,
} from 'app/components/commons/TimeRangeSection/utils';
import type { Hotel as ModelsHotel } from 'app/typings/builder/hotel';
import type { SpaceType } from 'app/typings/builder/spaceType';

type Hotel = Pick<ModelsHotel, 'spaces' | 'checkInTime' | 'checkOutTime'>;

export type SpacesFormData = Omit<SpaceValueType, 'desk' | 'spaces'> & {
  desk: IDeskFormatted;
  spaces: ISpace[];
};

export const CATEGORIES = [
  'wellness',
  'food',
  'social',
  'outdoors',
  'entertainment',
];

export const formatSpacesFormFromAPI = (
  hotel: Hotel,
  spaceTypes: SpaceType[]
) => {
  const defaultSpaces = hotel.spaces || [];
  const spaces = defaultSpaces
    .filter(
      (space) =>
        space.spaceTypeId !==
        spaceTypes.find((type) => type.name === 'Desk')?.id
    )
    .map((space) => ({
      ...space,
      pictures: space.pictures || [],
      openingHours: parseOpeningHoursFromAPI(space.openingHours),
    }))
    .reduce<Record<string, ISpace[]>>((acc, space) => {
      const type = spaceTypes.find(
        (spaceType) => spaceType.id === space.spaceTypeId
      );

      if (type) {
        acc[type.category] = acc[type.category] || [];
        acc[type.category].push(space as unknown as ISpace);
      }

      return acc;
    }, {});
  const sortedSpaces: ISpace[] = CATEGORIES.reduce((acc: ISpace[], cat) => {
    if (cat in spaces) {
      // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
      const sortedSpaces = spaces[cat].sort(
        (a, b) => a.spaceTypeId - b.spaceTypeId
      );

      return [...acc, ...sortedSpaces];
    }

    return acc;
  }, []);

  return {
    desk: {
      ...defaultSpaces.find(
        (space) =>
          space.spaceTypeId ===
          spaceTypes.find((type) => type.name === 'Desk')?.id
      ),
      checkInTime: hotel.checkInTime
        ? dayjs(hotel.checkInTime, 'HH:mm')
        : undefined,
      checkOutTime: hotel.checkOutTime
        ? dayjs(hotel.checkOutTime, 'HH:mm')
        : undefined,
    },
    spaces: sortedSpaces,
  };
};

export const formatNewSpaces = (
  newSpaces: number[],
  values: SpacesFormData,
  spaceTypes: SpaceType[],
  addedSpace: boolean
) => {
  const addedSpaces = newSpaces.map((spaceTypeId) => ({
    spaceTypeId,
    pictures: [],
    openingHours: parseOpeningHoursFromAPI(undefined),
    addedSpace,
  }));
  const allSpaces: ISpace[] = [
    ...values.spaces,
    ...(addedSpaces as unknown as ISpace[]),
  ];
  const spaces = allSpaces.reduce<Record<string, ISpace[]>>((acc, space) => {
    const type = spaceTypes.find(
      (spaceType) => spaceType.id === space.spaceTypeId
    );

    if (type) {
      acc[type.category] = acc[type.category] || [];
      acc[type.category].push(space);
    }

    return acc;
  }, {});
  const sortedSpaces: ISpace[] = CATEGORIES.reduce((acc: ISpace[], cat) => {
    if (cat in spaces) {
      // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
      const sortedSpaces = spaces[cat].sort(
        (a, b) => a.spaceTypeId - b.spaceTypeId
      );

      return [...acc, ...sortedSpaces];
    }

    return acc;
  }, []);

  return {
    ...values,
    spaces: sortedSpaces,
  };
};

const findAndFormatDeletedSpaces = (
  hotel: Hotel | undefined,
  spaces: ISpace[]
) => {
  if (!hotel?.spaces) {
    return [];
  }

  return hotel.spaces
    .filter(
      (space) =>
        space.spaceTypeId > 2 && !spaces.find((sp) => sp.id === space.id)
    )
    .map((space) => ({
      ...space,
      isDeleted: true,
    }));
};

export const reformatSpacesFormForAPI = (
  values: SpacesFormData,
  hotel: Hotel | undefined,
  spaceTypes: SpaceType[]
) => {
  const formattedFilteredSpaces = values.spaces
    .filter(
      (space) =>
        space.spaceTypeId !==
        spaceTypes.find((type) => type.name === 'Desk')?.id
    )
    .map((space) => ({
      ...space,
      openingHours: space.openingHours
        ? formatOpeningHoursForAPI(
            space.openingHours as unknown as FormOpeningHours
          )
        : undefined,
    }));

  return {
    checkInTime: dayjs(values.desk.checkInTime).format('HH:mm:ss'),
    checkOutTime: dayjs(values.desk.checkOutTime).format('HH:mm:ss'),
    spaces: [
      {
        spaceTypeId: 2,
        id: hotel?.spaces?.find((space) => space.spaceTypeId === 2)?.id,
        floor: values.desk.floor,
        phone: values.desk.phone,
      },
      ...formattedFilteredSpaces,
      ...findAndFormatDeletedSpaces(hotel, values.spaces),
    ],
  };
};
